import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { LogOut, Pencil, Plus, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { CATEGORIES, STATUS, formatPrice } from "@/lib/whatsapp";
import WhatsAppIcon from "@/components/site/WhatsAppIcon";

const EMPTY_FORM = {
  name: "",
  brand: "",
  category: "maroquinerie",
  price: "",
  condition: "Excellent état",
  reference: "",
  taille: "",
  matiere: "",
  description: "",
  image_url: "",
  status: "available",
  colors: [],
  images: [],
  accessories: [],
  retail_price: "",
};

const ACCESSORY_OPTIONS = ["Boîte", "Dustbag", "Certificat d'authenticité", "Reçu"];

function LoginView({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/login", { email, password });
      onSuccess();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="card-frame w-full max-w-md p-8 sm:p-10"
      >
        <div className="mb-8 flex justify-center">
          <img src="/logo-dark.png" alt="Zuliany" className="h-24 w-24 object-contain" />
        </div>
        <p className="eyebrow mb-2">Espace Pro</p>
        <h1 className="font-serif text-3xl text-white">Connexion admin</h1>
        <p className="mt-2 text-sm font-light text-zinc-500">
          Accès réservé à la gestion de la collection.
        </p>

        <div className="mt-8 space-y-5">
          <div className="space-y-2">
            <Label htmlFor="admin-email" className="text-zinc-300">Email</Label>
            <Input
              id="admin-email"
              data-testid="admin-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@zuliany.luxury"
              className="border-gold/20 bg-[#0A0A0B] text-white placeholder:text-zinc-600 focus-visible:ring-gold"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="admin-password" className="text-zinc-300">Mot de passe</Label>
            <Input
              id="admin-password"
              data-testid="admin-passcode-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••"
              className="border-gold/20 bg-[#0A0A0B] text-white placeholder:text-zinc-600 focus-visible:ring-gold"
            />
          </div>
          {error && (
            <p data-testid="admin-login-error" className="border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
              {error}
            </p>
          )}
          <Button
            type="submit"
            data-testid="admin-login-button"
            disabled={loading}
            className="w-full bg-gold-gradient font-mono text-xs font-semibold uppercase tracking-[0.2em] text-black hover:opacity-90"
          >
            {loading ? "Connexion…" : "Se connecter"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function ProductForm({ open, editing, form, setForm, onClose, onSave, uploading, onUpload }) {
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const [newColor, setNewColor] = useState("");
  const [newImage, setNewImage] = useState("");
  const [dragOver, setDragOver] = useState(null);
  const dragFrom = useRef(null);

  const allPhotos = [form.image_url, ...(form.images || [])].filter(Boolean);
  const setAllPhotos = (arr) =>
    setForm({ ...form, image_url: arr[0] || "", images: arr.slice(1) });
  const removePhoto = (i) => setAllPhotos(allPhotos.filter((_, j) => j !== i));
  const reorderPhotos = (from, to) => {
    if (from == null || from === to) return;
    const arr = [...allPhotos];
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    setAllPhotos(arr);
  };
  const addPhotoUrl = () => {
    const u = newImage.trim();
    if (!u) return;
    setAllPhotos([...allPhotos, u]);
    setNewImage("");
  };

  const addColor = () => {
    const name = newColor.trim();
    if (!name) return;
    if (form.colors.some((c) => c.name.toLowerCase() === name.toLowerCase())) {
      toast.error("Couleur déjà ajoutée");
      return;
    }
    setForm({ ...form, colors: [...form.colors, { name, available: true }] });
    setNewColor("");
  };

  const toggleColor = (i) =>
    setForm({
      ...form,
      colors: form.colors.map((c, j) => (j === i ? { ...c, available: !c.available } : c)),
    });

  const removeColor = (i) =>
    setForm({ ...form, colors: form.colors.filter((_, j) => j !== i) });

  const updateColor = (i, field, value) =>
    setForm({
      ...form,
      colors: form.colors.map((c, j) => (j === i ? { ...c, [field]: value } : c)),
    });

  const removeColorPhoto = (i, pIdx) =>
    setForm({
      ...form,
      colors: form.colors.map((c, j) => {
        if (j !== i) return c;
        const all = [c.photo, ...(c.photos || [])].filter(Boolean);
        all.splice(pIdx, 1);
        return { ...c, photo: all[0] || "", photos: all.slice(1) };
      }),
    });

  const inputCls = "border-gold/20 bg-[#0A0A0B] text-white placeholder:text-zinc-600 focus-visible:ring-gold";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        data-testid="product-dialog"
        className="max-h-[90vh] overflow-y-auto border-gold/25 bg-[#121215] sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-white">
            {editing ? "Modifier l'article" : "Nouvel article"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-zinc-300">Nom de la pièce *</Label>
              <Input data-testid="form-name-input" value={form.name} onChange={set("name")} placeholder="Birkin 25 Togo" className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Marque *</Label>
              <Input data-testid="form-brand-input" value={form.brand} onChange={set("brand")} placeholder="Hermès" className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Catégorie *</Label>
              <select
                data-testid="form-category-select"
                value={form.category}
                onChange={set("category")}
                className="h-10 w-full border border-gold/20 bg-[#0A0A0B] px-3 text-sm text-white focus:outline-none focus:ring-1 focus:ring-gold"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Prix (€) *</Label>
              <Input data-testid="form-price-input" type="number" min="0" step="1" value={form.price} onChange={set("price")} placeholder="12500" className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">État</Label>
              <Input data-testid="form-condition-input" value={form.condition} onChange={set("condition")} placeholder="Excellent état" className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Statut</Label>
              <select
                data-testid="form-status-select"
                value={form.status}
                onChange={set("status")}
                className="h-10 w-full border border-gold/20 bg-[#0A0A0B] px-3 text-sm text-white focus:outline-none focus:ring-1 focus:ring-gold"
              >
                {Object.entries(STATUS).map(([id, s]) => (
                  <option key={id} value={id}>{s.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">
                {form.category === "echarpe" ? "Dimension (ex : 90 × 90 cm)" : "Taille / Pointure"}
              </Label>
              <Input
                data-testid="form-taille-input"
                value={form.taille}
                onChange={set("taille")}
                placeholder={
                  form.category === "echarpe"
                    ? "Ex : 90 × 90 cm — affichée au choix de la couleur"
                    : "Ex : 40 IT ou 41 EU — affichée à côté de la marque"
                }
                className={inputCls}
              />
            </div>
            {form.category === "echarpe" && (
              <div className="space-y-2">
                <Label className="text-zinc-300">Matière (ex : Soie twill)</Label>
                <Input
                  data-testid="form-matiere-input"
                  value={form.matiere}
                  onChange={set("matiere")}
                  placeholder="Ex : Soie twill — affichée au choix de la couleur"
                  className={inputCls}
                />
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-300">Référence (ex : M75900)</Label>
            <Input
              data-testid="form-reference-input"
              value={form.reference}
              onChange={set("reference")}
              placeholder="M75900"
              className={inputCls}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-300">Prix boutique (optionnel)</Label>
            <Input
              data-testid="form-retail-input"
              type="number"
              min="0"
              step="1"
              value={form.retail_price}
              onChange={set("retail_price")}
              placeholder="Affiché barré au-dessus de votre prix"
              className={inputCls}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-300">Accompagnements inclus</Label>
            <div className="flex flex-wrap gap-2" data-testid="form-accessories">
              {ACCESSORY_OPTIONS.map((a) => {
                const on = form.accessories.includes(a);
                return (
                  <button
                    type="button"
                    key={a}
                    data-testid={`form-accessory-${a.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    onClick={() =>
                      setForm({
                        ...form,
                        accessories: on
                          ? form.accessories.filter((x) => x !== a)
                          : [...form.accessories, a],
                      })
                    }
                    className={`border px-3 py-1.5 text-xs transition-colors ${
                      on
                        ? "border-gold bg-gold/10 text-gold"
                        : "border-zinc-600 text-zinc-400 hover:border-gold/50"
                    }`}
                  >
                    {on ? "✓" : "+"} {a}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-300">Description</Label>
            <Textarea data-testid="form-description-input" value={form.description} onChange={set("description")} placeholder="Cuir Togo, garniture dorée, full set…" className={inputCls} rows={3} />
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-300">Couleurs (le client choisira)</Label>
            {form.colors.length > 0 && (
              <div className="space-y-2" data-testid="admin-colors-list">
                {form.colors.map((c, i) => {
                  const colorPhotos = [c.photo, ...(c.photos || [])].filter(Boolean);
                  return (
                    <div key={i} className="border border-gold/15 bg-[#0A0A0B] p-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          data-testid={`admin-color-toggle-${i}`}
                          onClick={() => toggleColor(i)}
                          title="Cliquer : disponible / indisponible"
                          className={`min-w-[76px] px-2 py-1 text-left text-xs ${
                            c.available ? "text-gold" : "text-zinc-500 line-through"
                          }`}
                        >
                          {c.available ? "●" : "○"} {c.name}
                        </button>
                        <Input
                          data-testid={`admin-color-dimension-${i}`}
                          value={c.dimension}
                          onChange={(e) => updateColor(i, "dimension", e.target.value)}
                          placeholder="Dimension (ex : 90 × 90 cm)"
                          className="h-8 min-w-[130px] flex-1 border-gold/15 bg-[#121215] text-xs text-white placeholder:text-zinc-600 focus-visible:ring-gold"
                        />
                        <Input
                          data-testid={`admin-color-matiere-${i}`}
                          value={c.matiere}
                          onChange={(e) => updateColor(i, "matiere", e.target.value)}
                          placeholder="Matière (ex : Soie twill)"
                          className="h-8 min-w-[130px] flex-1 border-gold/15 bg-[#121215] text-xs text-white placeholder:text-zinc-600 focus-visible:ring-gold"
                        />
                        <button
                          type="button"
                          data-testid={`admin-color-remove-${i}`}
                          onClick={() => removeColor(i)}
                          className="px-1 text-zinc-600 transition-colors hover:text-red-400"
                        >
                          ×
                        </button>
                      </div>
                      <div
                        className="mt-2 flex flex-wrap items-center gap-2"
                        data-testid={`admin-color-photos-${i}`}
                      >
                        {colorPhotos.map((u, k) => (
                          <span
                            key={k}
                            className="relative h-10 w-10 overflow-hidden border border-gold/25"
                          >
                            <img src={u} alt="" className="h-full w-full object-cover" />
                            <button
                              type="button"
                              data-testid={`admin-color-photo-remove-${i}-${k}`}
                              onClick={() => removeColorPhoto(i, k)}
                              title="Retirer cette photo"
                              className="absolute right-0 top-0 bg-black/70 px-1 text-[10px] leading-none text-white"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                        <label
                          data-testid={`admin-color-photo-${i}`}
                          title="Ajouter une ou plusieurs photos pour cette couleur (visible quand le client la choisit)"
                          className="flex h-10 w-10 cursor-pointer items-center justify-center border border-dashed border-gold/40 text-lg text-gold"
                        >
                          +
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            className="hidden"
                            onChange={(e) => {
                              const files = Array.from(e.target.files || []);
                              files.forEach((f) => onUpload(f, i));
                              e.target.value = "";
                            }}
                          />
                        </label>
                        {colorPhotos.length === 0 && (
                          <span className="text-[10px] font-light text-zinc-600">
                            Ajoutez les photos de cette couleur — le client les
                            fera défiler en cliquant.
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="flex gap-2">
              <Input
                data-testid="admin-color-input"
                value={newColor}
                onChange={(e) => setNewColor(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addColor();
                  }
                }}
                placeholder="Ajouter une couleur (ex : Noir)"
                className={inputCls}
              />
              <Button
                type="button"
                data-testid="admin-color-add-button"
                onClick={addColor}
                size="sm"
                variant="outline"
                className="shrink-0 border-gold/40 px-4 text-gold hover:bg-gold hover:text-black"
              >
                Ajouter
              </Button>
            </div>
            <p className="text-[11px] font-light text-zinc-600">
              ● disponible · ○ barrée = indisponible pour les clients.
              Renseignez dimension et matière : elles s'affichent quand le
              client choisit la couleur.
            </p>
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-300">Photos — la 1ʳᵉ est la principale</Label>
            <div className="flex flex-wrap gap-2" data-testid="admin-photo-gallery">
              {allPhotos.map((u, i) => (
                <div
                  key={u + i}
                  draggable
                  onDragStart={() => (dragFrom.current = i)}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(i);
                  }}
                  onDragLeave={() => setDragOver((d) => (d === i ? null : d))}
                  onDrop={(e) => {
                    e.preventDefault();
                    reorderPhotos(dragFrom.current, i);
                    setDragOver(null);
                  }}
                  className={`relative h-16 w-16 overflow-hidden border transition-colors ${
                    dragOver === i ? "border-gold" : "border-gold/25"
                  } ${i === 0 ? "ring-1 ring-gold" : ""}`}
                >
                  <img src={u} alt="" className="h-full w-full object-cover" />
                  <span className="absolute left-0 top-0 bg-black/70 px-1 text-[9px] text-white">
                    {i + 1}
                  </span>
                  <button
                    type="button"
                    data-testid={`admin-photo-remove-${i}`}
                    onClick={() => removePhoto(i)}
                    className="absolute right-0 top-0 bg-black/70 px-1 text-[10px] leading-none text-white"
                  >
                    ×
                  </button>
                  {i === 0 && (
                    <span className="absolute inset-x-0 bottom-0 bg-gold/90 text-center text-[7px] uppercase tracking-wider text-black">
                      Principale
                    </span>
                  )}
                </div>
              ))}
              <label
                data-testid="admin-upload-button"
                title="Ajouter une ou plusieurs photos (JPG, PNG, WebP, HEIC…)"
                className="flex h-16 w-16 cursor-pointer flex-col items-center justify-center gap-0.5 border border-dashed border-gold/40 text-gold"
              >
                <span className="text-lg leading-none">+</span>
                <span className="text-[8px] uppercase tracking-wider">
                  {uploading ? "…" : "Photo"}
                </span>
                <input
                  data-testid="admin-photo-input"
                  type="file"
                  accept="image/*,.heic,.heif"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    files.forEach((f) => onUpload(f));
                    e.target.value = "";
                  }}
                />
              </label>
            </div>
            <div className="flex gap-2">
              <Input
                data-testid="form-image-input"
                value={newImage}
                onChange={(e) => setNewImage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addPhotoUrl();
                  }
                }}
                placeholder="Coller un lien d'image (https://…)"
                className={inputCls}
              />
              <Button
                type="button"
                data-testid="admin-photo-url-button"
                onClick={addPhotoUrl}
                size="sm"
                variant="outline"
                className="shrink-0 border-gold/40 px-4 text-gold hover:bg-gold hover:text-black"
              >
                +
              </Button>
            </div>
            <p className="text-[11px] font-light text-zinc-600">
              Glissez-déposez les vignettes pour changer l'ordre — il sera
              respecté sur le site.
            </p>
          </div>
          <div className="flex gap-2">
            {!editing && (
              <Button
                type="button"
                data-testid="admin-save-continue-button"
                onClick={() => onSave(true)}
                variant="outline"
                className="flex-1 border-gold/40 text-gold hover:bg-gold hover:text-black"
              >
                Ajouter et continuer
              </Button>
            )}
            <Button
              data-testid="admin-save-button"
              onClick={() => onSave(false)}
              className={`bg-gold-gradient font-mono text-xs font-semibold uppercase tracking-[0.2em] text-black hover:opacity-90 ${
                editing ? "w-full" : "flex-1"
              }`}
            >
              {editing ? "Enregistrer les modifications" : "Ajouter"}
            </Button>
          </div>
          {!editing && (
            <p className="text-center text-[11px] font-light text-zinc-600">
              « Ajouter et continuer » garde le formulaire ouvert pour enchaîner
              plusieurs écharpes d'affilée.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Admin() {
  const [checking, setChecking] = useState(true);
  const [user, setUser] = useState(null);
  const [products, setProducts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [uploading, setUploading] = useState(false);

  const loadProducts = () =>
    api.get("/products").then((r) => setProducts(r.data)).catch(() => toast.error("Chargement impossible"));

  const uploadFile = async (file, colorIndex = null) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.path}`;
      if (colorIndex == null) {
        setForm((f) =>
          f.image_url
            ? { ...f, images: [...(f.images || []), url] }
            : { ...f, image_url: url }
        );
        toast.success("Photo téléversée");
      } else {
        setForm((f) => ({
          ...f,
          colors: f.colors.map((c, j) =>
            j === colorIndex
              ? c.photo
                ? { ...c, photos: [...(c.photos || []), url] }
                : { ...c, photo: url }
              : c
          ),
        }));
        toast.success("Photo de la couleur ajoutée");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        loadProducts();
      })
      .catch(() => setUser(null))
      .finally(() => setChecking(false));
  }, []);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      brand: p.brand,
      category: p.category,
      price: String(p.price),
      condition: p.condition,
      reference: p.reference || "",
      taille: p.taille || "",
      matiere: p.matiere || "",
      description: p.description,
      image_url: p.image_url,
      status: p.status,
      colors: p.colors || [],
      images: p.images || [],
      accessories: p.accessories || [],
      retail_price: p.retail_price ? String(p.retail_price) : "",
    });
    setDialogOpen(true);
  };

  const save = async (keepOpen = false) => {
    if (!form.name.trim() || !form.brand.trim() || !form.price) {
      toast.error("Nom, marque et prix sont requis");
      return;
    }
    const payload = {
      ...form,
      price: parseFloat(form.price),
      retail_price: form.retail_price ? parseFloat(form.retail_price) : 0,
    };
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Article mis à jour");
        setDialogOpen(false);
      } else {
        const { headers } = await api.post("/products", payload);
        toast.success(
          headers && headers["x-merged"]
            ? "Couleur ajoutée à la fiche existante"
            : "Article ajouté à la collection"
        );
        if (keepOpen) {
          setForm((f) => ({
            ...EMPTY_FORM,
            brand: f.brand,
            category: f.category,
            condition: f.condition,
            status: f.status,
          }));
          setEditing(null);
        } else {
          setDialogOpen(false);
        }
      }
      loadProducts();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const markSold = async (p, sold) => {
    const status = sold ? "sold" : "available";
    try {
      await api.put(`/products/${p.id}`, {
        ...p,
        price: parseFloat(p.price),
        retail_price: p.retail_price || 0,
        status,
      });
      setProducts((prev) =>
        prev.map((x) => (x.id === p.id ? { ...x, status } : x))
      );
      toast.success(
        sold ? "Marqué vendu — suppression automatique dans 30 jours" : "De nouveau disponible"
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer « ${p.brand} ${p.name} » définitivement ?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      setProducts((prev) => prev.filter((x) => x.id !== p.id));
      toast.success("Article supprimé");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0A0A0B]">
        <img src="/logo-dark.png" alt="Zuliany" className="h-20 w-20 animate-pulse object-contain" />
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <Toaster position="top-center" theme="dark" />
        <div className="grain-overlay" aria-hidden="true" />
        <LoginView onSuccess={() => { setUser(true); loadProducts(); }} />
      </>
    );
  }

  const availableCount = products.filter((p) => p.status === "available").length;

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-[#F4F4F6]">
      <Toaster position="top-center" theme="dark" />
      <header className="sticky top-0 z-40 border-b border-gold/15 bg-[#0A0A0B]/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <img src="/logo-dark.png" alt="Zuliany" className="h-10 w-10 object-contain" />
            <span className="font-serif text-lg tracking-[0.18em] text-white">ESPACE PRO</span>
            <span className="hidden font-mono text-[11px] text-zinc-500 sm:inline">/ {user === true ? "admin" : user.email}</span>
          </div>
          <div className="flex items-center gap-3">
            <Button
              data-testid="admin-add-product-button"
              onClick={openCreate}
              size="sm"
              className="bg-gold-gradient font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-black hover:opacity-90"
            >
              <Plus className="mr-1 h-4 w-4" /> Ajouter un article
            </Button>
            <Button
              data-testid="admin-logout-button"
              onClick={logout}
              size="sm"
              variant="outline"
              className="border-zinc-700 text-zinc-400 hover:border-gold/50 hover:bg-transparent hover:text-gold"
            >
              <LogOut className="mr-1 h-4 w-4" /> Quitter
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        <div className="mb-8 flex flex-wrap items-center gap-6 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
          <span data-testid="admin-count-total">{products.length} pièces</span>
          <span className="text-emerald-400">{availableCount} disponibles</span>
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto text-gold hover:underline"
          >
            Voir le site ↗
          </a>
        </div>

        {products.length === 0 ? (
          <div className="border border-dashed border-gold/25 px-6 py-16 text-center">
            <p className="font-serif text-2xl text-white">Aucun article pour l'instant</p>
            <p className="mt-2 text-sm font-light text-zinc-500">
              Cliquez sur « Ajouter un article » pour créer votre première pièce.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {products.map((p) => {
              const status = STATUS[p.status] || STATUS.available;
              return (
                <div
                  key={p.id}
                  data-testid={`admin-product-row-${p.id}`}
                  className="card-frame flex flex-wrap items-center gap-4 p-4"
                >
                  <img
                    src={p.image_url}
                    alt=""
                    className="h-16 w-16 shrink-0 object-cover"
                    onError={(e) => (e.target.style.visibility = "hidden")}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">{p.brand}</p>
                    <p className="truncate font-serif text-lg text-white">{p.name}</p>
                    <p className="text-xs font-light text-zinc-500">
                      {formatPrice(p.price)} · {p.condition}
                    </p>
                  </div>
                  <label
                    data-testid={`admin-sold-label-${p.id}`}
                    className="flex cursor-pointer items-center gap-2 border border-gold/20 px-2 py-1.5 text-xs text-zinc-300"
                  >
                    <input
                      type="checkbox"
                      data-testid={`admin-sold-checkbox-${p.id}`}
                      checked={p.status === "sold"}
                      onChange={(e) => markSold(p, e.target.checked)}
                      className="h-4 w-4 accent-[#D4AF37]"
                    />
                    Vendu
                  </label>
                  <span className={`hidden border px-2 py-1 font-mono text-[10px] uppercase tracking-[0.15em] sm:inline ${status.badge}`}>
                    {status.label}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      data-testid={`admin-edit-button-${p.id}`}
                      onClick={() => openEdit(p)}
                      size="sm"
                      variant="outline"
                      className="border-zinc-700 text-zinc-300 hover:border-gold/60 hover:bg-transparent hover:text-gold"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      data-testid={`admin-delete-button-${p.id}`}
                      onClick={() => remove(p)}
                      size="sm"
                      variant="outline"
                      className="border-red-900/60 text-red-400 hover:border-red-500 hover:bg-red-500/10 hover:text-red-300"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p className="mt-10 flex items-center justify-center gap-2 text-center font-mono text-[11px] text-zinc-600">
          <WhatsAppIcon className="h-3.5 w-3.5 text-gold/50" />
          Les visiteurs commandent directement via WhatsApp sur la page publique.
        </p>
      </main>

      <ProductForm
        open={dialogOpen}
        editing={editing}
        form={form}
        setForm={setForm}
        onClose={() => setDialogOpen(false)}
        onSave={save}
        uploading={uploading}
        onUpload={uploadFile}
      />
    </div>
  );
}
